import React, { useEffect, useState } from "react";
// XLSX
import * as XLSX from "xlsx-js-style";
// ANT DESIGN COMPONENTS
import {
  Table,
  Tag,
  Button,
  InputNumber,
  Tooltip,
  Input,
  Switch,
  Select,
  Upload,
  Modal,
} from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faCircleCheck,
  faXmarkCircle,
  faListNumeric,
  faSortAlphaAsc,
  faToggleOff,
  faDownload,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateAdapter } from "../../../../redux/adapters/adapterSlice";
import { updateOpenDrawerAssignInterfaceAdapter } from "../../../../redux/keys/keysSlice";
import {
  useDeleteAdapterKeyMutation,
  usePostAdapterKeyMutation,
  usePutAdapterKeyMutation,
} from "../../../../redux/keys/keysAPI";
// AUTHORIZATION
import GETJwtToken from "../../../../redux/authentication/authentication";
// INTERFACES
import { RootState } from "../../../../app/store";
import { MessageInterface } from "../../../../interfaces/Message.interface";
import {
  KeysAdapterKeyTableInterface,
  KeyFloorIndexing,
  KEY_FLOOR_INDEXING,
} from "../../../../interfaces/Key.interface";
import { GenericJsonInterface } from "../../../../interfaces/Global.interface";
import { AdapterInterface } from "../../../../interfaces/Adapter.interface";
import { AdapterProtocol } from "../../../../flatbuffers/generated/archive/adapter-protocol";
// COMPONENTS
import TableNoDataBox from "../../../components/table/TableNoDataBox";
import GlobalAlert2 from "../../../home/GlobalAlert2";
import SelectMessageType from "../../../components/select/SelectMessageType";
import ButtonSquareSave from "../../../components/buttons/ButtonSquareSave";
import ButtonSquareCode from "../../../components/buttons/ButtonSquareCode";
import TableColumnLiftType from "../../../components/table/TableColumnLiftType";
import KeysConfirmSaveTableData from "../../keys/KeysConfirmSaveTableData";
// UTILS
import { getTableRowClass, generateExcelAndDownloadDefinitions } from "../../../../utils/utils";
import {
  buildCustomKeysObject,
  buildAssignedKeysobject,
  toKeyFloorIndexing,
  FLOOR_INDEXING_INHERIT,
} from "../../keys/utils";

// Helpers for lower-threshold FBS field assignment
const clearLowerFbs = (row: KeysAdapterKeyTableInterface) => {
  Object.assign(row, {
    lower_encoded_type: null,
    lower_message_content: null,
    lower_message_value: null,
    lower_fbs_device_type: null,
    lower_message_label: null,
  });
};

const assignLowerFbs = (
  target: KeysAdapterKeyTableInterface,
  source: KeysAdapterKeyTableInterface,
) => {
  Object.assign(target, {
    lower_encoded_type: source.encoded_type,
    lower_message_content: source.idType,
    lower_message_value: source.message_value,
    lower_fbs_device_type: source.fbs_device_type,
    lower_message_label: source.message,
  });
};

export default function KeysTable({
  adapter,
  messages,
  isLoading,
  totalRowsGetAllMessages,
  isErrorGetAllMessages,
  errorMessageGetAllMessages,
  setIdMessageTypeSelected,
}: {
  isLoading: boolean;
  adapter: AdapterInterface;
  messages: MessageInterface[];
  totalRowsGetAllMessages: number;
  isErrorGetAllMessages: boolean;
  errorMessageGetAllMessages: any;
  setIdMessageTypeSelected: (id: any) => void;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Column } = Table;
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const { Option } = Select;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [totalRows, setTotalRows] = useState<number>(0);
  const [data, setData] = useState<KeysAdapterKeyTableInterface[]>([]);
  const [interfaceKeys, setInterfaceKeys] = useState<{ name: string; type: string }[]>([]);
  const [openModalConfirmSave, setOpenModalConfirmSave] = useState<boolean>(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerDeleteKey,
    {
      isLoading: isLoadingDeleteKey,
      isSuccess: isSuccessDeleteKey,
      error: errorDeleteKey,
      isError: isErrorDeleteKey,
    },
  ] = useDeleteAdapterKeyMutation();

  const [
    triggerPostKey,
    {
      isSuccess: isSuccessPostKey,
      isLoading: isLoadingPostKey,
      isError: isErrorPostKey,
      error: errorPostKey,
    },
  ] = usePostAdapterKeyMutation();

  const [
    triggerPutKey,
    {
      isSuccess: isSuccessPutKey,
      isError: isErrorPutKey,
      error: errorPutKey,
      isLoading: isLoadingPutKey,
    },
  ] = usePutAdapterKeyMutation();

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const buildFirstData = (adapterInterfaceKeys: GenericJsonInterface) => {
    const newData = buildCustomKeysObject(
      messages,
      adapterInterfaceKeys,
      String(adapter.protocolType),
    );
    if (newData && newData.length > 0 && adapter && adapter.keys) {
      const copy = buildAssignedKeysobject(newData, adapter.keys);
      setData(copy);
    } else {
      setData(newData);
    }
  };

  const getMessagesAndKeysData = async () => {
    if (messages) {
      // Log the messages array received from backend
      // eslint-disable-next-line no-console
      setTotalRows(totalRowsGetAllMessages);
      if (adapter && adapter.interfaceJson) {
        let parsedInterface: Record<string, unknown> = {};
        try {
          parsedInterface =
            typeof adapter.interfaceJson === "string" ? JSON.parse(adapter.interfaceJson) : {};
        } catch {
          parsedInterface = {};
        }
        // Log the adapter.keys array received from backend
        // eslint-disable-next-line no-console
        const result: { name: string; type: string }[] = Object.entries(parsedInterface).map(
          ([key, value]) => ({
            name: key,
            // eslint-disable-next-line no-nested-ternary
            type: Array.isArray(value) ? "array" : value === null ? "null" : typeof value,
          }),
        );
        buildFirstData(parsedInterface as GenericJsonInterface);
        setInterfaceKeys(result);
      }
    }
  };

  const onClickDelete = async (item: KeysAdapterKeyTableInterface) => {
    const token = await GETJwtToken();
    triggerDeleteKey({ id: item.idKey, token });
  };

  const isObjectContentValid = (record: KeysAdapterKeyTableInterface): boolean => {
    const excludedKeys = new Set([
      "isValid",
      "idKey",
      "methodType",
      "idMessage",
      "message",
      "type",
      "idType",
      "encoded_type",
      "message_value",
      "fbs_device_type",
      "lower_encoded_type",
      "lower_message_content",
      "lower_message_value",
      "lower_fbs_device_type",
      "lower_message_label",
      "floor_indexing",
      "debounce_time",
    ]);
    let rowIsValid = true;
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(record)) {
      if (!excludedKeys.has(key)) {
        if (value === null || value === undefined) {
          rowIsValid = false;
        }
      }
    }

    return rowIsValid;
  };

  const onChangeInputNumber = (
    newValue: number,
    name: string,
    record: KeysAdapterKeyTableInterface,
  ) => {
    const copy = structuredClone(data);
    const index = copy.findIndex((item) => item.idMessage === record.idMessage);

    if (index < 0) return;

    if (name === "bit_offset" || name === "byte_offset") {
      if (name === "bit_offset") {
        copy[index].bit_offset = newValue || 0;
      } else if (name === "byte_offset") {
        copy[index].byte_offset = newValue || 0;
      }
      const current = copy[index];

      const sameOffsets = (a: KeysAdapterKeyTableInterface, b: KeysAdapterKeyTableInterface) =>
        a.bit_offset &&
        a.byte_offset &&
        b.bit_offset &&
        b.byte_offset &&
        a.bit_offset === b.bit_offset &&
        a.byte_offset === b.byte_offset;

      copy.forEach((keyAdapter) => {
        if (!sameOffsets(current, keyAdapter)) return;

        const isDifferent = keyAdapter.idMessage !== current.idMessage;

        if (isDifferent) {
          if (keyAdapter.data) {
            const idx = copy.findIndex((k) => k.idMessage === keyAdapter.idMessage);
            if (idx >= 0) {
              if (current.data) {
                clearLowerFbs(copy[idx]);
              } else {
                assignLowerFbs(copy[idx], current);
              }
            }
          }
          return;
        }

        if (!current.data) {
          clearLowerFbs(current);
          return;
        }

        copy.forEach((ka2) => {
          if (!sameOffsets(current, ka2)) return;
          if (ka2.idMessage === current.idMessage) return;

          const idx2 = copy.findIndex((k) => k.idMessage === ka2.idMessage);
          if (idx2 < 0) return;

          if (!copy[idx2].data) {
            assignLowerFbs(current, ka2);
          } else {
            clearLowerFbs(current);
          }
        });
      });

      copy.forEach((obj) => {
        const duplicates = copy.filter((o) => sameOffsets(obj, o));

        if (duplicates.length <= 1) {
          clearLowerFbs(obj);
        }
      });

      current.isValid = isObjectContentValid(current);

      setData(copy);
      return;
    }

    copy[index][name] = newValue || 0;
    copy[index].isValid = isObjectContentValid(copy[index]);

    setData(copy);
  };

  const onChangeInputString = (
    newValue: string,
    name: string,
    record: KeysAdapterKeyTableInterface,
  ) => {
    const copy = structuredClone(data);
    const index = copy.findIndex(
      (item: KeysAdapterKeyTableInterface) => item.idMessage === record.idMessage,
    );
    if (index >= 0) {
      copy[index][name] = newValue;
      copy[index].isValid = isObjectContentValid(copy[index]);
    }
    setData(copy);
  };

  const onChangeInputBoolean = (
    newValue: boolean,
    name: string,
    record: KeysAdapterKeyTableInterface,
  ) => {
    const copy = structuredClone(data);
    const index = copy.findIndex(
      (item: KeysAdapterKeyTableInterface) => item.idMessage === record.idMessage,
    );
    if (index >= 0) {
      if (name === "units_based") {
        copy[index].units_based = newValue;
        copy[index].floors_based = !newValue;
      } else if (name === "floors_based") {
        copy[index].units_based = !newValue;
        copy[index].floors_based = newValue;
      } else if (name.toLowerCase() === "data") {
        const sameOffsets = (a: KeysAdapterKeyTableInterface, b: KeysAdapterKeyTableInterface) =>
          a.bit_offset &&
          a.byte_offset &&
          b.bit_offset &&
          b.byte_offset &&
          a.bit_offset === b.bit_offset &&
          a.byte_offset === b.byte_offset;

        copy.forEach((keyAdapter: KeysAdapterKeyTableInterface) => {
          const current = copy[index];
          if (!sameOffsets(current, keyAdapter)) return;
          const isDifferentMessage = keyAdapter.idMessage !== current.idMessage;
          if (isDifferentMessage) {
            if (keyAdapter.data) {
              const indexKeyAdapter = copy.findIndex((k) => k.idMessage === keyAdapter.idMessage);
              if (indexKeyAdapter >= 0) {
                if (newValue) {
                  clearLowerFbs(copy[indexKeyAdapter]);
                } else {
                  assignLowerFbs(copy[indexKeyAdapter], current);
                }
              }
            }
            return;
          }
          if (!newValue) {
            clearLowerFbs(current);
            return;
          }
          copy.forEach((ka2: KeysAdapterKeyTableInterface) => {
            if (!sameOffsets(current, ka2)) return;
            if (ka2.idMessage === current.idMessage) return;
            const indexKA2 = copy.findIndex((k) => k.idMessage === ka2.idMessage);
            if (indexKA2 < 0) return;
            if (!copy[indexKA2].data) {
              assignLowerFbs(current, ka2);
            } else {
              clearLowerFbs(current);
            }
          });
        });
        copy[index][name] = newValue;
      } else {
        copy[index][name] = newValue;
      }
      copy[index].isValid = isObjectContentValid(copy[index]);
    }
    setData(copy);
  };

  const onChangeSelectContentType = (newValue: number, record: KeysAdapterKeyTableInterface) => {
    const copy = structuredClone(data);
    const index = copy.findIndex(
      (item: KeysAdapterKeyTableInterface) => item.idMessage === record.idMessage,
    );
    if (index >= 0) {
      copy[index].contentType = newValue;
      copy[index].isValid = isObjectContentValid(copy[index]);
      if ("byte_offset" in record && newValue === 2) {
        copy[index].byte_offset = 0;
      }
    }
    setData(copy);
  };

  // Debounce time editing now handled here (was in MessagesTable)
  const onChangeDebounceTime = (newValue: number | null, record: KeysAdapterKeyTableInterface) => {
    const copy = structuredClone(data);
    const index = copy.findIndex((item) => item.idMessage === record.idMessage);
    if (index >= 0) {
      copy[index].debounce_time = typeof newValue === "number" ? newValue : 0;
      copy[index].isValid = isObjectContentValid(copy[index]);
      setData(copy);
    }
  };

  const onChangeFloorIndexing = (
    newValue: KeyFloorIndexing,
    record: KeysAdapterKeyTableInterface,
  ) => {
    const copy = structuredClone(data);
    const index = copy.findIndex((item) => item.idMessage === record.idMessage);
    if (index >= 0) {
      copy[index].floor_indexing = newValue;
      copy[index].isValid = isObjectContentValid(copy[index]);
      setData(copy);
    }
  };

  const onBulkSetFloorIndexing = (categoryIdType: number, newValue: KeyFloorIndexing) => {
    const copy = structuredClone(data);
    copy.forEach((_, idx) => {
      if (copy[idx].idType === categoryIdType) {
        copy[idx].floor_indexing = newValue;
        copy[idx].isValid = isObjectContentValid(copy[idx]);
      }
    });
    setData(copy);
  };

  const onClickSave = async () => {
    const token = await GETJwtToken();

    // Save keys data (including debounce_time)
    const requests = data.map(async (item: KeysAdapterKeyTableInterface) => {
      if (item.isValid) {
        const newValue = interfaceKeys.reduce((acc: any, key: any) => {
          acc[key.name] = item[key.name];
          return acc;
        }, {});
        const requestBody = {
          adapter_id: adapter.id,
          encoded_type: item.encoded_type,
          message_content: item.idType,
          message_value: item.message_value,
          fbs_device_type: item.fbs_device_type,
          position: null,
          content_type: item.contentType,
          lower_encoded_type: item.lower_encoded_type ?? null,
          lower_message_content: item.lower_message_content ?? null,
          lower_message_value: item.lower_message_value ?? null,
          lower_fbs_device_type: item.lower_fbs_device_type ?? null,
          lower_position: null,
          units_based: item.units_based,
          floors_based: item.floors_based,
          floor_indexing: item.floor_indexing ?? null,
          value: JSON.stringify(newValue),
          debounce_time: item.debounce_time ?? null,
        };
        return item.methodType === "POST"
          ? triggerPostKey({ token, body: requestBody }).unwrap()
          : triggerPutKey({
              token,
              id: item.idKey,
              body: requestBody,
            }).unwrap();
      }
      return true;
    });
    await Promise.all(requests);
  };

  const onChangeMessageType = (newValue: string, id: number) => {
    setIdMessageTypeSelected(id);
  };

  const handleOpenAssignInterface = () => {
    dispatch(updateAdapter(adapter));
    dispatch(updateOpenDrawerAssignInterfaceAdapter(true));
  };

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const binaryStr = e.target?.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData: KeysAdapterKeyTableInterface[] = XLSX.utils.sheet_to_json(sheet);
        setData(jsonData);
      } catch (error2) {
        // eslint-disable-next-line no-console
        console.error("Error reading the Excel file. Please try again.");
      }
    };

    reader.readAsBinaryString(file); // Read the file as binary
    return false; // Prevent default upload behavior
  };

  const isFloorExpandedValue = (_columnName: string, record: any): boolean => {
    if (record.contentType !== 2) {
      return false;
    }

    if (record.floors_based === true) {
      return true;
    }

    return interfaceKeys.some(
      (key) =>
        key.type === "boolean" &&
        key.name.toLowerCase().includes("floor") &&
        record[key.name] === true,
    );
  };

  const disableDynamicInput = (columnName: string, record: any): boolean => {
    if (columnName === "byte_offset" && record.contentType === 2) {
      return true;
    }
    if (isFloorExpandedValue(columnName, record)) {
      return true;
    }
    return false;
  };

  const showLowMessageTable = () => {
    if (!data || data.length === 0) return false;
    return data.some((item) => item.lower_encoded_type !== null);
  };
  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (adapter && adapter.id && messages && messages.length > 0) {
      getMessagesAndKeysData();
    } else {
      setData([]);
    }
  }, [adapter, messages]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div
        className="generalStyles__pageHeader"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <div>
          <div className="generalStyles__flex">
            <TableColumnLiftType liftType={adapter.liftType} />
            <Tag color="orange">{adapter.protocolType}</Tag>
            <Tag color="cyan">{adapter.name || "--"}</Tag>
            <Tag color="geekblue">KEYS</Tag>
          </div>
        </div>
        <div className="generalStyles__flex ">
          <div className=" generalStyles__width100">
            <SelectMessageType setFilteredValue={onChangeMessageType} />
          </div>
          <div>
            <Button
              className="buttonStyle__13 generalStyles__mlFix"
              style={{ marginTop: "-5px" }}
              onClick={() => {
                generateExcelAndDownloadDefinitions(data, String(adapter.name ?? ""));
              }}
              loading={isLoading}
              icon={<FontAwesomeIcon className="" icon={faDownload} />}
            />
          </div>
          <div>
            <Upload accept=".xlsx, .xls" beforeUpload={handleUpload} showUploadList={false}>
              <Button
                className="buttonStyle__20  generalStyles__mlFix buttonStyle__normal"
                style={{ marginTop: "-5px" }}
                icon={<FontAwesomeIcon icon={faUpload} />}
              />
            </Upload>
          </div>
          <div>
            <ButtonSquareCode
              onClick={handleOpenAssignInterface}
              isLoading={isLoading}
              disabledButton={isLoading}
            />
          </div>
          <div>
            <ButtonSquareSave
              onClick={() => {
                setOpenModalConfirmSave(true);
              }}
              disabledButton={isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
      {data.length > 0 && (
        <div style={{ marginTop: 8, marginBottom: 4 }}>
          {Array.from(new Map(data.map((row) => [row.idType, row.type])).entries()).map(
            ([idType, typeName]) => (
              <span
                key={idType}
                style={{ marginRight: 16, display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Tag color="blue">{typeName}</Tag>
                <Select
                  size="small"
                  style={{ width: 160 }}
                  placeholder="Bulk floor indexing"
                  onChange={(value: string) => {
                    onBulkSetFloorIndexing(idType, toKeyFloorIndexing(value));
                  }}
                >
                  <Option value={FLOOR_INDEXING_INHERIT}>Inherit from adapter</Option>
                  <Option value={KEY_FLOOR_INDEXING.ZERO_BASED}>Zero-based</Option>
                  <Option value={KEY_FLOOR_INDEXING.ONE_BASED}>One-based</Option>
                </Select>
              </span>
            ),
          )}
        </div>
      )}
      <Table
        locale={{
          emptyText: (
            <>
              {isErrorGetAllMessages ? (
                <TableNoDataBox
                  isError={isErrorGetAllMessages}
                  errorMessage={errorMessageGetAllMessages}
                  noItemsFound="No actions found"
                />
              ) : (
                <div className="generalStyles__flexCenter" style={{ height: "300px" }}>
                  No Data
                </div>
              )}
            </>
          ),
        }}
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        dataSource={data}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
        rowKey={(record) => record.idMessage}
        size="small"
        className="mt-3"
        loading={isLoadingDeleteKey || isLoading || isLoadingPostKey || isLoadingPutKey}
        footer={() => (
          <div className="generalStyles__flexEnd">
            <Tag>{totalRows} Rows</Tag>
          </div>
        )}
      >
        <Column
          title={t("general.state")}
          dataIndex="state"
          key="state"
          align="center"
          render={(text, record: KeysAdapterKeyTableInterface) => (
            <>
              {record.isValid ? (
                <FontAwesomeIcon className="generalStyles__success" icon={faCircleCheck} />
              ) : (
                <Tooltip title={t("general.keyDuplicated")}>
                  <FontAwesomeIcon className="generalStyles__error" icon={faXmarkCircle} />
                </Tooltip>
              )}
            </>
          )}
        />
        <Column
          title={t("general.message")}
          dataIndex="message"
          key="message"
          render={(text) => <>{text}</>}
        />
        <Column
          title={t("general.type")}
          dataIndex="type"
          key="type"
          render={(text) => <>{text}</>}
        />
        <Column
          title={<span>Debounce (ms)</span>}
          dataIndex="debounce_time"
          key="debounce_time"
          render={(text, record: KeysAdapterKeyTableInterface) => (
            <InputNumber
              style={{ width: "100%" }}
              size="small"
              min={0}
              step={100}
              value={typeof record.debounce_time === "number" ? record.debounce_time : 0}
              onChange={(value) => onChangeDebounceTime(value, record)}
            />
          )}
        />
        <Column
          title={t("general.contentType")}
          dataIndex="contentType"
          key="contentType"
          render={(text, record: KeysAdapterKeyTableInterface) => (
            <Select
              className="generalStyles__width100"
              onChange={(value) => {
                onChangeSelectContentType(value, record);
              }}
              size="small"
              value={text}
            >
              {[
                { id: 1, name: "Bit Message" },
                { id: 2, name: "Byte Message" },
              ].map((item: { id: number; name: string }) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          )}
        />
        {showLowMessageTable() && (
          <Column
            title={t("general.lowMessage")}
            dataIndex="lower_message_label"
            key="lower_message_label"
            render={(text) => <span>{text || "--"}</span>}
          />
        )}
        {(
          [
            AdapterProtocol.CGA,
            AdapterProtocol.LOBBYVISION,
            AdapterProtocol.RVU,
            AdapterProtocol.LN2,
            AdapterProtocol.LN1,
          ] as number[]
        ).includes(adapter.protocolType) && (
          <Column
            title="Unit Based"
            dataIndex="units_based"
            key="units_based"
            render={(text, record: KeysAdapterKeyTableInterface) => (
              <Switch
                onChange={(e) => {
                  onChangeInputBoolean(e, "units_based", record);
                }}
                checked={text}
              />
            )}
          />
        )}
        {(
          [
            AdapterProtocol.CGA,
            AdapterProtocol.LOBBYVISION,
            AdapterProtocol.RVU,
            AdapterProtocol.LN2,
            AdapterProtocol.LN1,
          ] as number[]
        ).includes(adapter.protocolType) && (
          <Column
            title="Floor Based"
            dataIndex="floors_based"
            key="floors_based"
            render={(text, record: KeysAdapterKeyTableInterface) => (
              <Switch
                onChange={(e) => {
                  onChangeInputBoolean(e, "floors_based", record);
                }}
                checked={text}
              />
            )}
          />
        )}
        <Column
          title="Floor Indexing"
          dataIndex="floor_indexing"
          key="floor_indexing"
          render={(text, record: KeysAdapterKeyTableInterface) => (
            <Select
              className="generalStyles__width100"
              size="small"
              value={toKeyFloorIndexing(record.floor_indexing) ?? FLOOR_INDEXING_INHERIT}
              onChange={(value: string) => {
                onChangeFloorIndexing(toKeyFloorIndexing(value), record);
              }}
            >
              <Option value={FLOOR_INDEXING_INHERIT}>Inherit from adapter</Option>
              <Option value={KEY_FLOOR_INDEXING.ZERO_BASED}>Zero-based</Option>
              <Option value={KEY_FLOOR_INDEXING.ONE_BASED}>One-based</Option>
            </Select>
          )}
        />
        {interfaceKeys.map((item: { name: string; type: string }) => (
          <Column
            title={
              <span>
                {item.type === "number" && (
                  <Tooltip title="Number">
                    <FontAwesomeIcon icon={faListNumeric} style={{ marginRight: 6 }} />{" "}
                  </Tooltip>
                )}
                {item.type === "string" && (
                  <Tooltip title="Text">
                    <FontAwesomeIcon icon={faSortAlphaAsc} style={{ marginRight: 6 }} />{" "}
                  </Tooltip>
                )}
                {item.type === "boolean" && (
                  <Tooltip title="Boolean">
                    <FontAwesomeIcon icon={faToggleOff} style={{ marginRight: 6 }} />{" "}
                  </Tooltip>
                )}
                {item.name}
              </span>
            }
            dataIndex={item.name}
            key={item.name}
            render={(text, record: KeysAdapterKeyTableInterface) => (
              <>
                {item.type === "number" &&
                  (isFloorExpandedValue(item.name, record) ? (
                    <span style={{ opacity: 0.5 }}>Auto</span>
                  ) : (
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      size="small"
                      precision={0}
                      value={record[item.name]}
                      onChange={(e) => {
                        onChangeInputNumber(e, item.name, record);
                      }}
                      disabled={disableDynamicInput(item.name, record)}
                    />
                  ))}
                {item.type === "string" &&
                  (isFloorExpandedValue(item.name, record) ? (
                    <span style={{ opacity: 0.5 }}>Auto</span>
                  ) : (
                    <Input
                      size="small"
                      placeholder={item.name}
                      value={record[item.name]}
                      onChange={(event: React.FormEvent<HTMLInputElement>) => {
                        onChangeInputString(event.currentTarget.value, item.name, record);
                      }}
                      disabled={disableDynamicInput(item.name, record)}
                    />
                  ))}
                {item.type === "boolean" && (
                  <Switch
                    onChange={(e) => {
                      onChangeInputBoolean(e, item.name, record);
                    }}
                    checked={record[item.name]}
                  />
                )}
              </>
            )}
          />
        ))}
        {/** DELETE */}
        <Column
          title="Delete"
          dataIndex="delete"
          key="delete"
          render={(text, record: KeysAdapterKeyTableInterface) => (
            <Button
              onClick={() => {
                onClickDelete(record);
              }}
              shape="circle"
              className={!record.isValid ? "" : "buttonStyle__26"}
              disabled={!record.isValid || record.methodType === "POST"}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          )}
        />
      </Table>
      <GlobalAlert2
        isError={isErrorDeleteKey}
        isSuccess={isSuccessDeleteKey}
        requestType="DELETE"
        error={errorDeleteKey}
        name="Key"
      />
      <GlobalAlert2
        isError={isErrorPostKey}
        isSuccess={isSuccessPostKey}
        requestType="POST"
        error={errorPostKey}
        name="Key"
      />
      <GlobalAlert2
        isError={isErrorPutKey}
        isSuccess={isSuccessPutKey}
        requestType="PUT"
        error={errorPutKey}
        name="Key"
      />
      <Modal
        title={t("general.confirmSave")}
        open={openModalConfirmSave}
        onOk={() => {
          onClickSave();
          setOpenModalConfirmSave(false);
        }}
        onCancel={() => {
          setOpenModalConfirmSave(false);
        }}
        width="75%"
        bodyStyle={{ overflowX: "auto" }}
      >
        <KeysConfirmSaveTableData data={data} componentType="keys" />
      </Modal>
    </div>
  );
}
